<script setup lang="ts">
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'
import type { ReturnTile } from '../../property'
import type { NextStep } from '../constants/investFlow'

import InvestOrderForm, { type InvestQuote } from './InvestOrderForm.vue'
import InvestEligibilityBlocker, { type EligibilityFix } from './InvestEligibilityBlocker.vue'
import InvestPlacingOrder from './InvestPlacingOrder.vue'
import InvestOrderConfirmed from './InvestOrderConfirmed.vue'

// Body of the invest drawer — picks what to show for the current step.
// Loading / error / eligibility blockers take precedence over the form.

export type InvestStep = 'details' | 'review' | 'placing' | 'success'

export interface ConfirmedOrder {
  id: string
  orderReference: string
  tokens: number
  stakeLabel: string
  totalPaid: number
  nextSteps: NextStep[]
}

interface Props {
  step: InvestStep
  loading: boolean
  propertyError: boolean
  eligibilityFix: EligibilityFix | null
  propertyImage: string
  assetName: string
  location: string
  totalTokens: number
  slicealPrice: number | null
  minInvestmentAmount: number | null
  maxInvestmentAmount: number | null
  tokensAvailable: number
  sliderMax: number
  quote: InvestQuote
  isQuoting: boolean
  feesError: string | null
  returnTiles: ReturnTile[]
  walletBalance: number
  orderError: string | null
  isInsufficientBalance: boolean
  placingStep: number
  confirmedOrder: ConfirmedOrder | null
  formInstanceKey: number
}

defineProps<Props>()

const tokens = defineModel<number>('tokens', { required: true })

const emit = defineEmits<{
  (e: 'eligibility-cta'): void
  (e: 'continue'): void
  (e: 'place-order'): void
  (e: 'open-terms'): void
  (e: 'add-funds'): void
  (e: 'view-order'): void
  (e: 'back-to-properties'): void
}>()
</script>

<template>
  <Box direction="col" class="invest-panel">
    <Box v-if="loading" direction="col" gap="6" class="invest-panel__loading">
      <Skeleton animation="wave" width="100%" height="332px" border-radius="20px" />
      <Skeleton animation="wave" width="100%" height="137px" border-radius="20px" />
      <Skeleton animation="wave" width="100%" height="221px" border-radius="20px" />
    </Box>

    <Box
      v-else-if="propertyError"
      direction="col"
      gap="3"
      align="center"
      justify="center"
      class="invest-panel__error"
    >
      <PiIcon name="exclamation-circle" class="invest-panel__error-icon" />
      <Text type="body" align="center">We couldn't load this property. Close and try again.</Text>
    </Box>

    <InvestOrderConfirmed
      v-else-if="step === 'success' && confirmedOrder"
      :asset-name="assetName"
      :tokens="confirmedOrder.tokens"
      :stake-label="confirmedOrder.stakeLabel"
      :total-paid="confirmedOrder.totalPaid"
      :order-reference="confirmedOrder.orderReference"
      :next-steps="confirmedOrder.nextSteps"
      @view-order="emit('view-order')"
      @back-to-properties="emit('back-to-properties')"
    >
      <template #documents>
        <slot name="order-documents" />
      </template>
    </InvestOrderConfirmed>

    <InvestPlacingOrder
      v-else-if="step === 'placing'"
      :active-step="placingStep"
      :total-committed="quote.netAmount"
    />

    <InvestEligibilityBlocker
      v-else-if="eligibilityFix"
      :fix="eligibilityFix"
      :property-image="propertyImage"
      :property-title="assetName"
      :property-location="location"
      @cta="emit('eligibility-cta')"
    />

    <InvestOrderForm
      v-else
      :key="`invest-form-${formInstanceKey}`"
      v-model:tokens="tokens"
      :mode="step === 'review' ? 'review' : 'details'"
      :asset-name="assetName"
      :total-tokens="totalTokens"
      :sliceal-price="slicealPrice"
      :min-investment-amount="minInvestmentAmount"
      :max-investment-amount="maxInvestmentAmount"
      :tokens-available="tokensAvailable"
      :slider-max="sliderMax"
      :quote="quote"
      :is-quoting="isQuoting"
      :fees-error="feesError"
      :return-tiles="returnTiles"
      :wallet-balance="walletBalance"
      :order-error="orderError"
      :is-insufficient-balance="isInsufficientBalance"
      :submitting="false"
      @continue="emit('continue')"
      @place-order="emit('place-order')"
      @open-terms="emit('open-terms')"
      @add-funds="emit('add-funds')"
    />
  </Box>
</template>

<style scoped>
.invest-panel {
  min-height: 100%;
  background: var(--p-surface-50);
}

.invest-panel__loading {
  flex: 1;
  padding: 24px 16px;
}

.invest-panel__error {
  flex: 1;
  padding: 2rem 1.5rem;
}

.invest-panel__error-icon {
  font-size: 28px;
  color: var(--p-danger-500);
}
</style>
